import { Badge, Button, Drawer, Skeleton } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { isApiError } from "../../api/client";
import { cancelIntent, getIntent, recheckIntent, type IntentEnvelope } from "../../api/payments";
import { getBillInvoice } from "../../api/invoices";
import { useServices } from "../../app/services";
import { useAppContext, useMoney, useScope } from "./hooks";
import { billing as t } from "./strings";
import { QrCode } from "./QrCode";
import s from "./billing.module.css";

export const POLL_MS = 3000;

/** Payee shown beside the QR, read from the URI itself so it always matches what the code carries. */
function uriParams(uri: string): { payee: string | null } {
  try {
    const q = new URL(uri).searchParams;
    return { payee: q.get("pn") || q.get("pa") };
  } catch { return { payee: null }; }
}

/**
 * UPI QR / payment link drawer. Polls the intent every 3 s while it is open and still waiting; stops when closed, settled,
 * or past its expiry. Test stub only: nothing here reaches a live provider.
 * A UPI request is drawn as a QR code (generated locally) with the same URI as text below it; a payment link is text only.
 */
export function IntentDrawer({ intentId, onClose }: { intentId: string; onClose: (settled: boolean) => void }) {
  const ctx = useAppContext();
  const { client, scopeKey } = useScope();
  const { now } = useServices();
  const { fmt } = useMoney(ctx);
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const key = ["intent", ...scopeKey, intentId];

  const q = useQuery<IntentEnvelope>({
    queryKey: key,
    queryFn: ({ signal }) => getIntent(client, intentId, signal),
    retry: false,
    staleTime: 0,
    refetchInterval: (query) => {
      const i = query.state.data?.intent;
      if (!i || i.status !== "created") return false;
      return i.expiresAt !== null && now() >= i.expiresAt ? false : POLL_MS;
    },
  });
  const intent = q.data?.intent;
  const expired = !!intent && intent.status === "created" && intent.expiresAt !== null && now() >= intent.expiresAt;
  const settled = intent?.status === "captured";
  const canEdit = ctx.has("billing.payment_intent_manage"); // Recheck and Cancel check this lever on the API
  const set = (r: IntentEnvelope) => qc.setQueryData(key, r);
  const recheck = useMutation({ mutationFn: () => recheckIntent(client, intentId), onSuccess: set });
  const cancel = useMutation({ mutationFn: () => cancelIntent(client, intentId), onSuccess: set });
  const invoice = useQuery({ queryKey: ["bill-invoice", ...scopeKey, intent?.billId], queryFn: () => getBillInvoice(client, intent!.billId), enabled: settled, retry: false });

  const title = intent?.method === "payment_link" ? t.upi.linkTitle : t.upi.title;
  const upiParams = intent?.method === "upi_qr" ? uriParams(intent.urlOrUri) : null;
  const err = q.error ?? recheck.error ?? cancel.error;
  const copy = async () => {
    try { await navigator.clipboard.writeText(intent!.urlOrUri); setCopied(true); } catch { /* clipboard unavailable: the text is selectable */ }
  };

  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose(!!settled); }} title={title} description={intent ? fmt(intent.amount) : undefined}
      footer={<Button onClick={() => onClose(!!settled)}>{t.upi.close}</Button>}>
      <div className={s.pad}>
        {q.isPending ? <><Skeleton height={20} /><Skeleton height={80} /></> : null}
        {err ? <p role="alert" className={s.err}>{isApiError(err) ? err.message : t.errors.generic}</p> : null}
        {intent ? (
          <>
            <p className={s.muted}>{t.upi.stub}</p>
            {intent.status === "created" && !expired ? (
              <>
                <p>{t.upi.scan}</p>
                {intent.method === "upi_qr" ? (
                  <div className={s.qrBlock}>
                    <QrCode value={intent.urlOrUri} label={t.upi.qrLabel(fmt(intent.amount))} />
                    <p className={s.qrAmount}>{fmt(intent.amount)}</p>
                    {upiParams?.payee ? <p className={s.muted}>{t.upi.payee(upiParams.payee)}</p> : null}
                  </div>
                ) : null}
                <div className={s.qr} data-testid="intent-target">{intent.urlOrUri}</div>
                <div className={s.payrow}>
                  <Button as="a" href={intent.urlOrUri} target="_blank" rel="noopener noreferrer">{t.upi.open}</Button>
                  <Button onClick={() => void copy()}>{copied ? t.upi.copied : t.upi.copy}</Button>
                </div>
                <p role="status" aria-live="polite">{t.upi.waiting}</p>
                {canEdit ? (
                  <div className={s.payrow}>
                    <Button onClick={() => recheck.mutate()} busy={recheck.isPending} busyLabel="Checking…">{t.upi.recheck}</Button>
                    <Button onClick={() => cancel.mutate()} busy={cancel.isPending} busyLabel="Cancelling…">{t.upi.cancel}</Button>
                  </div>
                ) : null}
              </>
            ) : null}
            {settled ? (
              <>
                <Badge tone="ok" dot role="status">{t.upi.paid}</Badge>
                {invoice.data ? <Button as={Link} to={`/billing/invoices/${invoice.data.id}`} variant="primary">{t.upi.printInvoice}</Button> : null}
              </>
            ) : null}
            {expired || intent.status === "expired" ? <Badge tone="warn" role="status">{t.upi.expired}</Badge> : null}
            {intent.status === "cancelled" ? <Badge tone="neutral" role="status">{t.upi.cancelled}</Badge> : null}
            {intent.status === "failed" ? <Badge tone="crit" role="status">{t.upi.failed}</Badge> : null}
            {intent.status === "needs_review" ? <Badge tone="warn" role="status">{t.upi.review}</Badge> : null}
          </>
        ) : null}
      </div>
    </Drawer>
  );
}
